import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Cpu } from "lucide-react";

import Spinner from "../../components/spinner";
import Pagination from "../../components/pagination";
import DateRangeInput from "../../components/dateRangeInput";
import { useLanguage } from "../../context/languageContext";

/**
 * AI Activity — every request this platform has made to a model.
 *
 * Written by the backend's `runChat`, which is the one function every AI call
 * passes through, so nothing can generate without appearing here: the task,
 * the provider and model, the tokens it used, how long it took, who triggered
 * it, and the error when it failed.
 *
 * Read-only. These rows are a record of what happened and of what it cost;
 * a record that can be edited answers neither question.
 */

const API = process.env.REACT_APP_API_ROOT;
const PAGE_SIZE = 25;

const TASK_LABELS = {
  summaryGeneration: "Summary generation",
  overviewGeneration: "Overview generation",
  translation: "Translation",
  overviewTranslation: "Overview translation",
  overviewTranslationReview: "Overview translation review",
  aboutTheBookTranslation: "About the Book translation",
};

const num = (n) => Number(n || 0).toLocaleString("en-US");

const when = (value) => {
  if (!value) return "—";
  return new Date(value)
    .toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    })
    .replace(",", "");
};

const Stat = ({ label, value, tone = "" }) => (
  <div className="box_wrapper flex-fill" style={{ minWidth: 150 }}>
    <div className={`fs-4 ${tone}`} style={{ fontVariantNumeric: "tabular-nums" }}>{value}</div>
    <div className="text-muted small">{label}</div>
  </div>
);

const AiUsage = () => {
  const { adminInfo } = useLanguage();

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [task, setTask] = useState("");
  const [model, setModel] = useState("");
  const [status, setStatus] = useState("");
  const [dates, setDates] = useState("");

  // Sent to both endpoints, so the totals above the table describe the rows in
  // it rather than the whole collection.
  const filters = useMemo(() => {
    const [from = "", to = ""] = String(dates).split(",");
    return {
      task: task || undefined,
      model: model || undefined,
      status: status || undefined,
      from: from || undefined,
      to: to || undefined,
    };
  }, [task, model, status, dates]);

  const load = useCallback(async () => {
    if (!adminInfo?.token) return;
    setLoading(true);
    const headers = { Authorization: `Bearer ${adminInfo.token}` };
    try {
      const [list, totals] = await Promise.all([
        axios.get(`${API}/api/admin/ai-usage`, { headers, params: { ...filters, page, limit: PAGE_SIZE } }),
        axios.get(`${API}/api/admin/ai-usage/summary`, { headers, params: filters }),
      ]);

      if (list.data?.status) {
        setRows(list.data.data?.items || []);
        setTotal(list.data.data?.total || 0);
        setPages(list.data.data?.pages || 1);
      }
      if (totals.data?.status) setSummary(totals.data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Could not load the AI activity.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [adminInfo?.token, filters, page]);

  useEffect(() => { load(); }, [load]);

  // A filter change starts again at page 1 — page 4 of the old result set is
  // not page 4 of the new one. Done in the setter rather than in an effect:
  // an effect runs AFTER the fetch effect, so it fired one request against
  // the stale page and a second against the reset one.
  const applyFilter = (set) => (value) => {
    set(value);
    setPage(1);
  };

  const totals = summary?.totals;
  const options = summary?.options || { tasks: [], models: [] };

  return (
    <div className="p-3">
      <h4 className="mb-1">AI Activity</h4>
      <p className="text-muted small mb-3">
        Every request this platform has made to a model — what it was for, what it used, and who
        asked for it. Written by the server itself, so nothing generates without appearing here.
      </p>

      <div className="d-flex flex-wrap gap-2 mb-3">
        <Stat label="Requests" value={num(totals?.requests)} />
        <Stat label="Failed" value={num(totals?.failed)} tone={totals?.failed ? "text-danger" : ""} />
        <Stat label="Tokens in" value={num(totals?.promptTokens)} />
        <Stat label="Tokens out" value={num(totals?.completionTokens)} />
        <Stat
          label={
            totals?.streamed
              ? `Tokens total · ${num(totals.streamed)} streamed, uncounted`
              : "Tokens total"
          }
          value={num(totals?.totalTokens)}
        />
        <Stat label="Average time" value={totals?.ms ? `${(totals.ms / 1000).toFixed(1)}s` : "—"} />
      </div>

      {summary?.byModel?.length > 0 && (
        <div className="box_wrapper mb-3">
          <div className="text-muted small mb-2">Tokens by model</div>
          <div className="d-flex flex-wrap gap-3">
            {summary.byModel.map((m) => (
              <div key={`${m.provider}/${m.model}`} className="small">
                <span className="fw-medium">{m.model}</span>
                <span className="text-muted"> · {m.provider} · {num(m.requests)} requests · </span>
                <span>{num(m.totalTokens)} tokens</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="d-flex flex-column flex-lg-row gap-2 mb-3">
        <select className="form-control form-control-sm" style={{ height: 40, maxWidth: 220 }}
          value={task} onChange={(e) => applyFilter(setTask)(e.target.value)} aria-label="Task">
          <option value="">All tasks</option>
          {options.tasks.map((t) => <option key={t} value={t}>{TASK_LABELS[t] || t}</option>)}
        </select>

        <select className="form-control form-control-sm" style={{ height: 40, maxWidth: 220 }}
          value={model} onChange={(e) => applyFilter(setModel)(e.target.value)} aria-label="Model">
          <option value="">All models</option>
          {options.models.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>

        <select className="form-control form-control-sm" style={{ height: 40, maxWidth: 160 }}
          value={status} onChange={(e) => applyFilter(setStatus)(e.target.value)} aria-label="Status">
          <option value="">Any outcome</option>
          <option value="ok">Succeeded</option>
          <option value="failed">Failed</option>
        </select>

        <DateRangeInput className="form-control form-control-sm" ariaLabel="When"
          value={dates} onChange={applyFilter(setDates)} />
      </div>

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <div className="text-center text-muted py-5">
          <Cpu size={28} className="mb-2" />
          <p className="mb-0">
            {task || model || status || dates
              ? "No AI requests match that."
              : "No AI requests recorded yet."}
          </p>
        </div>
      ) : (
        <>
          <div className="table_scrollbar table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th className="index">S.no</th>
                  <th>When</th>
                  <th>Task</th>
                  <th>Book</th>
                  <th className="max_width">What for</th>
                  <th>Model</th>
                  <th>Tokens</th>
                  <th>Took</th>
                  <th>Triggered by</th>
                  <th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row._id}>
                    <td>{(page - 1) * PAGE_SIZE + i + 1}</td>
                    <td>{when(row.createdAt)}</td>
                    <td>
                      {TASK_LABELS[row.task] || row.task}
                      {row.language ? <div className="text-muted small">{row.language}</div> : null}
                    </td>
                    {/* Which book the spend was on — the first question asked
                        of a cost report, and the one the label alone could not
                        answer for a translation. */}
                    <td>{row.targetName || <span className="text-muted">—</span>}</td>
                    <td className="max_width">
                      {row.label || <span className="text-muted">—</span>}
                      {row.error ? <div className="text-danger small">{row.error}</div> : null}
                    </td>
                    <td>
                      {row.model}
                      <div className="text-muted small">
                        {row.provider}
                        {row.reasoningEffort ? ` · ${row.reasoningEffort}` : ""}
                        {/* A borrowed config names whose version ran:
                            "inherited:translation" + 5 → "v5 of Translation". */}
                        {row.configVersion
                          ? row.configSource?.startsWith("inherited:")
                            ? ` · v${row.configVersion} of ${TASK_LABELS[row.configSource.slice(10)] || row.configSource.slice(10)}`
                            : ` · v${row.configVersion}`
                          : ""}
                      </div>
                    </td>
                    <td style={{ fontVariantNumeric: "tabular-nums" }}>
                      {/* A streamed reply carries no usage totals. Saying so is
                          honest; showing 0 would read as "free". */}
                      {row.streamed ? (
                        <span className="text-muted">streamed</span>
                      ) : row.totalTokens == null ? (
                        <span className="text-muted">—</span>
                      ) : (
                        <>
                          {num(row.totalTokens)}
                          <div className="text-muted small">
                            {num(row.promptTokens)} in · {num(row.completionTokens)} out
                          </div>
                        </>
                      )}
                    </td>
                    <td>{row.ms ? `${(row.ms / 1000).toFixed(1)}s` : "—"}</td>
                    <td>
                      {row.adminName || <span className="text-muted">Background</span>}
                      {row.adminEmail ? <div className="text-muted small">{row.adminEmail}</div> : null}
                    </td>
                    <td>
                      {row.status === "ok" ? (
                        <span className="text-success">Succeeded</span>
                      ) : (
                        <span className="text-danger">Failed</span>
                      )}
                      {/* "length" means it ran out of output tokens — the usual
                          cause of an empty answer, worth naming on the row. */}
                      {row.finishReason && row.finishReason !== "stop" ? (
                        <div className="text-warning small">{row.finishReason}</div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={pages}
              totalItems={total}
              itemsPerPage={PAGE_SIZE}
              onPageChange={setPage}
              showItemsPerPage={false}
              showTotalItems
            />
          )}
        </>
      )}
    </div>
  );
};

export default AiUsage;
